/**
 * MobileFAB Component — Edge Tab Design
 *
 * Semi-circle tab flush with left screen edge, draggable vertically.
 * Replaces header + bottom nav on mobile to maximize content space.
 * Tap = open navigation menu. Drag = reposition along edge.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle, BookOpen, Film, Calendar, Settings, LogOut, X, User } from 'lucide-react';
import './MobileFAB.css';

const navItems = [
  { id: 'chat', label: 'Coaching', icon: MessageCircle },
  { id: 'notes', label: 'Notes', icon: BookOpen },
  { id: 'video', label: 'Video', icon: Film },
  { id: 'planning', label: 'Plan', icon: Calendar },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const DRAG_THRESHOLD = 10;
const STORAGE_KEY = 'lumi-fab-y';
const FAB_HEIGHT = 48;

function getInputAlignedY() {
  const input = document.querySelector('.input-container');
  if (input) {
    const rect = input.getBoundingClientRect();
    return Math.round(rect.top + rect.height / 2);
  }
  return Math.round(window.innerHeight - 80);
}

function clampY(y) {
  return Math.max(60, Math.min(window.innerHeight - 60, y));
}

function MobileFAB({ activeTab, onTabChange, onSettingsClick, userName, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [fabY, setFabY] = useState(null); // null = calculate on mount
  const [isSnapping, setIsSnapping] = useState(false);
  const hasCustomPosition = useRef(!!localStorage.getItem(STORAGE_KEY));

  // Use refs for drag state to avoid stale closures
  const dragRef = useRef({ startY: 0, startFabY: 0, moved: 0, isDragging: false });

  // Set default position on mount and recalculate on resize/orientation change
  useEffect(() => {
    const recalc = () => {
      if (hasCustomPosition.current) {
        // User dragged — use saved position but clamp to viewport
        const saved = parseInt(localStorage.getItem(STORAGE_KEY), 10);
        setFabY(clampY(saved));
      } else {
        // No custom position — align with input box
        setFabY(getInputAlignedY());
      }
    };

    // Initial calculation (after DOM is ready)
    requestAnimationFrame(recalc);

    window.addEventListener('resize', recalc);
    window.addEventListener('orientationchange', () => setTimeout(recalc, 100));
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('orientationchange', recalc);
    };
  }, []);

  const handleItemClick = useCallback((itemId) => {
    if (itemId === 'settings') {
      onSettingsClick();
    } else {
      onTabChange(itemId);
    }
    setIsOpen(false);
  }, [onTabChange, onSettingsClick]);

  const handleLogout = useCallback(() => {
    setIsOpen(false);
    onLogout();
  }, [onLogout]);

  // Close menu on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Glow + tooltip on every page load
  useEffect(() => {
    setShowTooltip(true);
    const timer = setTimeout(() => setShowTooltip(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Touch drag handlers — use ref for isDragging to avoid stale closures
  const handleTouchStart = useCallback((e) => {
    if (isOpen) return;
    const touch = e.touches[0];
    dragRef.current = { startY: touch.clientY, startFabY: fabY, moved: 0, isDragging: false };
  }, [isOpen, fabY]);

  const handleTouchMove = useCallback((e) => {
    if (isOpen) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragRef.current.startY;
    dragRef.current.moved = Math.abs(deltaY);

    if (dragRef.current.moved > DRAG_THRESHOLD) {
      dragRef.current.isDragging = true;
      const newY = Math.max(60, Math.min(
        window.innerHeight - 60,
        dragRef.current.startFabY + deltaY
      ));
      setFabY(newY);
      e.preventDefault();
    }
  }, [isOpen]);

  const handleTouchEnd = useCallback(() => {
    if (dragRef.current.isDragging) {
      // Was a drag — save custom position
      hasCustomPosition.current = true;
      localStorage.setItem(STORAGE_KEY, String(fabY));
      setIsSnapping(true);
      setTimeout(() => setIsSnapping(false), 300);
    } else {
      // Was a tap — toggle menu
      setIsOpen(prev => !prev);
      setShowTooltip(false);
    }
    dragRef.current.isDragging = false;
  }, [fabY]);

  // Don't render until position is calculated
  if (fabY === null) return null;

  // Compute menu position based on FAB Y
  const menuStyle = {};
  const viewH = window.innerHeight;
  if (fabY > viewH * 0.6) {
    menuStyle.bottom = viewH - fabY + FAB_HEIGHT / 2 + 8;
    menuStyle.top = 'auto';
  } else {
    menuStyle.top = fabY + FAB_HEIGHT / 2 + 8;
    menuStyle.bottom = 'auto';
  }
  menuStyle.left = 48;

  const fabTop = fabY - FAB_HEIGHT / 2;

  return (
    <div className="mobile-fab-wrapper">
      {/* Backdrop */}
      {isOpen && (
        <div
          className="mobile-fab-backdrop"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Menu Panel — positioned dynamically */}
      <div
        className={`mobile-fab-menu ${isOpen ? 'open' : ''}`}
        style={menuStyle}
      >
        <nav aria-label="Navigation menu">
          {navItems.map((item, index) => {
            const IconComponent = item.icon;
            const isActive = item.id === activeTab;
            return (
              <button
                key={item.id}
                className={`mobile-fab-menu-item ${isActive ? 'active' : ''}`}
                onClick={() => handleItemClick(item.id)}
                style={{ transitionDelay: isOpen ? `${index * 30}ms` : '0ms' }}
              >
                <IconComponent size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="mobile-fab-menu-divider" />
        <div className="mobile-fab-user-section">
          <div className="mobile-fab-user-info">
            <User size={14} />
            <span>{userName || 'User'}</span>
          </div>
          <button className="mobile-fab-logout" onClick={handleLogout}>
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Edge Tab FAB — semi-circle flush left, draggable vertically */}
      <div
        className={`mobile-fab-button ${isOpen ? 'open' : ''} ${isSnapping ? 'snapping' : ''}`}
        style={{ top: fabTop }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="button"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
        tabIndex={0}
      >
        {isOpen ? (
          <X size={22} />
        ) : (
          <img src="/skate-fab.png" alt="Menu" className="mobile-fab-logo" />
        )}
      </div>

      {/* Tooltip — appears to the right of FAB */}
      {showTooltip && !isOpen && (
        <div className="mobile-fab-tooltip" style={{ top: fabTop + FAB_HEIGHT / 2 - 12 }}>
          Menu
        </div>
      )}
    </div>
  );
}

export default MobileFAB;
